import { useEffect, useState } from 'react';
import {
  CREDIT_GRANT_KINDS,
  EVENT_CEILING_TIER_DEFINITIONS,
  ceilingTierForGrantKind,
  type CreditGrantKind,
} from '@icebreaker/shared';
import { Button, Skeleton } from '@icebreaker/ui';
import { numericDateOptions } from '@/features/sales/model/sales-view';
import { useTranslation } from '@/shared/i18n/i18n-provider';
import { useAppDispatch, useAppSelector } from '@/shared/store/hooks';
import type { RootState } from '@/shared/store/store';
import { fetchCreditOverview } from '../store/credits.slice';
import { GrantCreditsDialog } from './grant-credits-dialog';
import styles from './organizations.module.css';

/** Türün açtığı etkinliğin katılımcı tavanı — tek tablodan (`EVENT_CEILING_TIER_DEFINITIONS`). */
export function ceilingOf(kind: CreditGrantKind): number | null {
  return EVENT_CEILING_TIER_DEFINITIONS[ceilingTierForGrantKind(kind)].attendeeCeiling;
}

/**
 * KREDİ ÖZETİ (P5 bölüm 3): kalan kredi (büyük tabular sayı), türe göre
 * dağılım, yalnız 12'li paketler için yanma satırları ve dolu birincil
 * "Kredi yükle". Bakiye sunucuda DEFTERDEN hesaplanır; burada toplanmaz.
 *
 * KREDİ BİTTİ: sayı 0 ve bekleme renginde, "Kredi yükle" birincil kalır.
 * Etkin sözleşme varken kredi harcanmaz; bu yüzden "bitti" uyarısı çizilmez.
 */
export function CreditSummaryCard({
  organizationId,
  organizationName,
  openGrantOnMount,
  onGrantOpened,
}: {
  organizationId: string;
  organizationName: string;
  openGrantOnMount: boolean;
  onGrantOpened: () => void;
}) {
  const t = useTranslation();
  const dispatch = useAppDispatch();
  const overview = useAppSelector((root: RootState) => root.panelCredits.overview);
  const [granting, setGranting] = useState(false);

  useEffect(() => {
    dispatch(fetchCreditOverview.request({ organizationId }));
  }, [dispatch, organizationId]);

  useEffect(() => {
    if (!openGrantOnMount) return;
    setGranting(true);
    onGrantOpened();
  }, [onGrantOpened, openGrantOnMount]);

  const data = overview.organizationId === organizationId ? overview.data : null;
  const grantButton = (
    <Button
      variant="primary"
      block
      className={styles.grantButton}
      onClick={() => setGranting(true)}
    >
      {t.t('admin.credit.grant')}
    </Button>
  );

  let body;
  if (!data && overview.phase === 'failed') {
    body = (
      <>
        <h2 className={styles.cardTitle}>{t.t('admin.credit.summaryTitle')}</h2>
        <div className={styles.sectionState} role="alert">
          <p className={styles.sectionError}>{t.t('admin.credit.loadError')}</p>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => dispatch(fetchCreditOverview.request({ organizationId }))}
          >
            {t.t('admin.orgs.retry')}
          </Button>
        </div>
      </>
    );
  } else if (!data) {
    body = (
      <>
        <h2 className={styles.cardTitle}>{t.t('admin.credit.summaryTitle')}</h2>
        <div className={styles.sectionLoading}>
          <Skeleton label={t.t('admin.credit.loading')} lines={5} />
        </div>
      </>
    );
  } else {
    const { summary } = data;
    const exhausted = summary.available === 0 && summary.activeContract === null;
    const burns = data.grants.filter(
      (grant) => grant.kind === 'pack_12' && !grant.expired && grant.remaining > 0,
    );
    const dateOptions = numericDateOptions(t.locale);
    body = (
      <div className={styles.summaryStrip}>
        <div>
          <h2 className={styles.cardTitle}>{t.t('admin.credit.summaryTitle')}</h2>
          <div className={styles.bigNumberRow}>
            <span className={[styles.bigNumber, exhausted ? styles.warnNumber : ''].join(' ')}>
              {t.formatNumber(summary.available)}
            </span>
            <span className={styles.bigNumberLabel}>
              {t.t('admin.credit.remainingLabel', { count: summary.available })}
            </span>
          </div>
        </div>
        <div>
          {CREDIT_GRANT_KINDS.map((kind) => (
            <div key={kind} className={styles.kindRow}>
              <span className={styles.kindText}>
                <span className={styles.kindName}>{t.t(`admin.credit.kinds.${kind}`)}</span>
                <span className={styles.kindDesc}>
                  {t.t(`admin.credit.kindDesc.${kind}`, {
                    ceiling: t.formatNumber(ceilingOf(kind) ?? 0),
                  })}
                </span>
              </span>
              <span className={styles.kindCount}>{t.formatNumber(summary.byKind[kind])}</span>
            </div>
          ))}
        </div>
        <div>
          {burns.length > 0 ? (
            <>
              <p className={styles.burnTitle}>{t.t('admin.credit.burnTitle')}</p>
              {burns.map((grant) => (
                <div key={grant.id} className={styles.burnRow}>
                  <span>
                    {t.t('admin.credit.burnBought', {
                      date: t.formatDate(grant.purchasedAt ?? grant.grantedAt, dateOptions),
                    })}
                  </span>
                  <strong>{t.t('admin.credit.burnLeft', { count: grant.remaining })}</strong>
                  {grant.expiresAt ? (
                    <span className={styles.burnExpires}>
                      {t.t('admin.credit.burnExpires', {
                        date: t.formatDate(grant.expiresAt, dateOptions),
                      })}
                    </span>
                  ) : null}
                </div>
              ))}
            </>
          ) : null}
          {summary.activeContract ? (
            <p className={styles.contractCovers}>
              {t.t('admin.credit.contractCovers', {
                date: t.formatDate(summary.activeContract.endsAt, dateOptions),
              })}
            </p>
          ) : null}
        </div>
        <div>
          {grantButton}
          {exhausted ? (
            <p className={styles.exhausted} role="status">
              <span className={styles.exhaustedTitle}>{t.t('admin.credit.exhaustedTitle')}</span>
              {' · '}
              {t.t('admin.credit.exhaustedBody')}
            </p>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.card}>
      {body}
      {granting ? (
        <GrantCreditsDialog
          organizationId={organizationId}
          organizationName={organizationName}
          onClose={() => setGranting(false)}
        />
      ) : null}
    </div>
  );
}
